// src/pages/TaskManager.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import "../styles/TaskManager.css";
import { DateTime } from "luxon";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { db } from "../firebase/config";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  limit,
  updateDoc,
  writeBatch, // ✅ add
} from "firebase/firestore";
import Lottie from "lottie-react";
import loadingAnimation from "../tools/Animations/Loading Dots Blue.json";
import NoDataIllustration from "../tools/assets/undraw_no-data_ig65.svg";

import { useAuth } from "../Context/AuthContext";

// ✅ Default fallback (used only if company hasn't created types yet)
const DEFAULT_TASK_TYPES = ["Cleaning"];

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const STATUSES = ["open", "in_progress", "completed", "archived"]; // completed = submitted-for-review
const REVIEW_STATUSES = ["not_submitted", "submitted", "approved", "rejected"];
const REVIEW_LABELS = {
  not_submitted: "Not submitted",
  submitted: "Under review",
  approved: "Approved",
  rejected: "Rejected",
};

function PageLoader({ label = "Loading…" }) {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
      }}
    >
      <Lottie
        animationData={loadingAnimation}
        loop
        style={{ width: 120, height: 120 }}
      />
      {label && <div style={{ color: "#64748b", fontSize: 14 }}>{label}</div>}
    </div>
  );
}

export default function TaskManager() {
  const { user } = useAuth();

  const [companyId, setCompanyId] = useState(null);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");

  const [loadingInit, setLoadingInit] = useState(true);
  const [savingTask, setSavingTask] = useState(false);

  const [employees, setEmployees] = useState({}); // id -> data
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState("all");
  const [selectedWorkSiteId, setSelectedWorkSiteId] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [selectedReview, setSelectedReview] = useState("all");

  const [showKpis, setShowKpis] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [allowMultiAssign, setAllowMultiAssign] = useState(false);

  const [taskTypes, setTaskTypes] = useState([]); // [{id, name}]
  const [loadingTypes, setLoadingTypes] = useState(true);

  const [showTypesModal, setShowTypesModal] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [savingType, setSavingType] = useState(false);
  const [deletingTypeId, setDeletingTypeId] = useState(null);

  const [locations, setLocations] = useState([]); // [{ id, name, ... }]
  const [loadingLocations, setLoadingLocations] = useState(true);

  // ✅ NEW: Details modal (row click)
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTask, setActiveTask] = useState(null);

  const openTaskDetails = (task) => {
    setActiveTask(task);
    setShowDetailsModal(true);
  };

  const closeTaskDetails = () => {
    setShowDetailsModal(false);
    setActiveTask(null);
  };

  // ✅ NEW: Proof preview modal
  const [showProofModal, setShowProofModal] = useState(false);
  const [activeProof, setActiveProof] = useState(null); // { url, name? }

  const openProofModal = (photo, idx = 0) => {
    if (!photo?.url) return;
    setActiveProof({
      url: photo.url,
      name: photo.name || `Photo ${idx + 1}`,
    });
    setShowProofModal(true);
  };

  const closeProofModal = () => {
    setShowProofModal(false);
    setActiveProof(null);
  };

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingTask, setRejectingTask] = useState(null);

  // ✅ Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // 10 / 20 / 50 etc

  const openRejectModal = (task) => {
    setRejectingTask(task);
    setRejectReason(task?.rejectionReason || "");
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectReason("");
    setRejectingTask(null);
  };

  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "Cleaning",
    priority: "Medium",
    assigneeId: "",
    assigneeIds: [],
    workSiteId: "",
    dueDate: "",
    photoRequired: false,

    // ✅ NEW (Checklist)
    checklistEnabled: false,
    checklistDraft: "",
    checklistItems: [], // [{ id, text }]
  });

  // ─────────────────────────────────────────────────────────────
  // Bootstrap
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.uid) return;

    (async () => {
      try {
        setLoadingInit(true);

        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data() || {};
        // ✅ restore show/hide KPIs
        const savedShowKpis = userData?.taskManagerShowKpis;
        if (typeof savedShowKpis === "boolean") setShowKpis(savedShowKpis);
        const cid = userData.companyId;

        if (!cid) {
          toast.error("Company not found for this user.");
          setLoadingInit(false);
          return;
        }
        setCompanyId(cid);

        // saved filters
        const saved = userData.taskManagerFilters;
        if (saved && saved.companyId === cid) {
          if (saved.selectedEmployeeId)
            setSelectedEmployeeId(saved.selectedEmployeeId);
          if (saved.selectedWorkSiteId)
            setSelectedWorkSiteId(saved.selectedWorkSiteId);
          if (saved.selectedStatus) setSelectedStatus(saved.selectedStatus);
          if (saved.selectedType) setSelectedType(saved.selectedType);
          if (saved.selectedReview) setSelectedReview(saved.selectedReview);

          if (saved.selectedPriority)
            setSelectedPriority(saved.selectedPriority);
        }

        const companyRef = doc(db, "companies", cid);
        const companySnap = await getDoc(companyRef);
        const companyData = companySnap.data() || {};
        setCompanyTimeZone(companyData.timeZone || "UTC");

        const employeesRef = collection(db, `companies/${cid}/employees`);
        const employeesSnap = await getDocs(employeesRef);
        const map = {};
        employeesSnap.forEach((d) => {
          map[d.id] = { id: d.id, ...d.data() };
        });
        setEmployees(map);

        setLoadingInit(false);
      } catch (e) {
        console.error("TaskManager bootstrap failed", e);
        toast.error("Unable to load Task Manager.");
        setLoadingInit(false);
      }
    })();
  }, [user?.uid]);

  // Persist filters
  useEffect(() => {
    if (!user?.uid || !companyId) return;

    const save = async () => {
      try {
        const userRef = doc(db, "users", user.uid);
        await setDoc(
          userRef,
          {
            taskManagerFilters: {
              companyId,
              selectedEmployeeId,
              selectedWorkSiteId,
              selectedStatus,
              selectedType,
              selectedPriority,
              selectedReview,
            },
          },
          { merge: true },
        );
      } catch (e) {
        console.error("Failed saving taskManagerFilters", e);
      }
    };

    save();
  }, [
    user?.uid,
    companyId,
    selectedEmployeeId,
    selectedWorkSiteId,
    selectedStatus,
    selectedType,
    selectedPriority,
    selectedReview, // ✅ add
  ]);

  useEffect(() => {
    if (!user?.uid) return;

    const save = async () => {
      try {
        await setDoc(
          doc(db, "users", user.uid),
          { taskManagerShowKpis: showKpis },
          { merge: true },
        );
      } catch (e) {
        console.error("Failed saving taskManagerShowKpis", e);
      }
    };

    save();
  }, [user?.uid, showKpis]);

  useEffect(() => {
    if (!companyId) return;

    setLoadingLocations(true);

    const ref = collection(db, `companies/${companyId}/locations`);
    // If you don’t have an index for orderBy('name'), remove orderBy for now.
    const qRef = query(ref, orderBy("name", "asc"));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((l) => l.active !== false); // show active only (optional)

        setLocations(list);
        setLoadingLocations(false);
      },
      (err) => {
        console.error("Locations onSnapshot failed", err);
        setLocations([]);
        setLoadingLocations(false);
      },
    );

    return () => unsub();
  }, [companyId]);

  // ─────────────────────────────────────────────────────────────
  // Derived options
  // ─────────────────────────────────────────────────────────────
  const employeeOptions = useMemo(() => {
    return Object.values(employees).sort((a, b) => {
      const na = a.name || a.fullName || a.displayName || "Unknown A";
      const nb = b.name || b.fullName || b.displayName || "Unknown B";
      return na.localeCompare(nb);
    });
  }, [employees]);

  const workSiteOptions = useMemo(() => {
    return (locations || [])
      .map((l) => ({
        id: l.id,
        name: l.name || l.address?.street || "Unnamed site",
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [locations]);

  // ─────────────────────────────────────────────────────────────
  // Live tasks subscription (NO Firestore where filters)
  // We fetch latest tasks, then filter client-side to avoid indexes.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;

    setLoadingTasks(true);
    setTasks([]); // clear when filters change so table can go empty immediately

    const baseRef = collection(db, `companies/${companyId}/tasks`);
    const qRef = query(baseRef, orderBy("createdAt", "desc"), limit(200));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const filtered = list.filter((t) => {
          // 1) Employee filter (assigneeIds contains)
          if (selectedEmployeeId !== "all") {
            if (selectedEmployeeId !== "all") {
              // ✅ new model
              if (t.assigneeId) {
                if (t.assigneeId !== selectedEmployeeId) return false;
              } else {
                // fallback legacy model
                const ids = Array.isArray(t.assigneeIds) ? t.assigneeIds : [];
                if (!ids.includes(selectedEmployeeId)) return false;
              }
            }
          }

          // 2) Job site
          if (selectedWorkSiteId !== "all") {
            if ((t.workSiteId || "") !== selectedWorkSiteId) return false;
          }

          // 3) Status
          if (selectedStatus !== "all") {
            if ((t.status || "open") !== selectedStatus) return false;
          }

          // 4) Type
          if (selectedType !== "all") {
            if ((t.type || "") !== selectedType) return false;
          }

          // 5) Priority
          if (selectedPriority !== "all") {
            if ((t.priority || "") !== selectedPriority) return false;
          }
          // 6) Review status
          if (selectedReview !== "all") {
            if ((t.reviewStatus || "not_submitted") !== selectedReview)
              return false;
          }

          return true;
        });

        setTasks(filtered);
        setLoadingTasks(false);
      },
      (err) => {
        console.error("Tasks onSnapshot failed", err);
        setTasks([]);
        setLoadingTasks(false);
        toast.error("Could not load tasks.");
      },
    );

    return () => unsub();
  }, [
    companyId,
    selectedEmployeeId,
    selectedWorkSiteId,
    selectedStatus,
    selectedType,
    selectedPriority,
    selectedReview,
  ]);

  useEffect(() => {
    if (!companyId) return;

    setLoadingTypes(true);

    const ref = collection(db, `companies/${companyId}/task_types`);
    const qRef = query(ref, orderBy("name", "asc"));

    const unsub = onSnapshot(
      qRef,
      async (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTaskTypes(list);
        setLoadingTypes(false);

        // ✅ Optional: auto-seed defaults if none exist
        if (snap.empty) {
          try {
            const batch = writeBatch(db);
            DEFAULT_TASK_TYPES.forEach((name) => {
              const docRef = doc(
                db,
                `companies/${companyId}/task_types`,
                name.toLowerCase().replace(/\s+/g, "_"),
              );
              batch.set(
                docRef,
                {
                  name,
                  isDefault: true,
                  createdAt: serverTimestamp(),
                  createdBy: user?.uid || null,
                },
                { merge: true },
              );
            });
            await batch.commit();
          } catch (e) {
            console.error("Seeding default task types failed", e);
          }
        }
      },
      (err) => {
        console.error("Task types onSnapshot failed", err);
        setTaskTypes([]);
        setLoadingTypes(false);
      },
    );

    return () => unsub();
  }, [companyId, user?.uid]);

  const taskTypeOptions = useMemo(() => {
    const names = (taskTypes || [])
      .map((t) => String(t.name || "").trim())
      .filter(Boolean);

    // fallback if none exist yet
    const merged = names.length ? names : DEFAULT_TASK_TYPES;

    // unique
    return Array.from(new Set(merged)).sort((a, b) => a.localeCompare(b));
  }, [taskTypes]);

  useEffect(() => {
    if (!taskTypeOptions.length) return;
    setForm((prev) => {
      if (!prev.type || !taskTypeOptions.includes(prev.type)) {
        return { ...prev, type: taskTypeOptions[0] };
      }
      return prev;
    });
  }, [taskTypeOptions]);

  const normalizeTypeId = (name) =>
    String(name || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s_-]/g, "")
      .replace(/\s+/g, "_")
      .slice(0, 40);

  const addTaskType = async () => {
    if (!companyId || !user?.uid) return;

    const name = String(newTypeName || "").trim();
    if (!name) return toast.error("Type name is required.");
    if (name.length > 40) return toast.error("Max 40 characters.");

    const exists = taskTypeOptions.some(
      (t) => t.toLowerCase() === name.toLowerCase(),
    );
    if (exists) return toast.error("That type already exists.");

    setSavingType(true);
    try {
      const id = normalizeTypeId(name);
      await setDoc(
        doc(db, `companies/${companyId}/task_types`, id),
        {
          name,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
        },
        { merge: true },
      );

      setNewTypeName("");
      toast.success("Task type added.");
    } catch (e) {
      console.error("addTaskType failed", e);
      toast.error("Could not add type.");
    } finally {
      setSavingType(false);
    }
  };

  const deleteTaskType = async (typeDoc) => {
    if (!companyId || !typeDoc?.id) return;

    // optional safeguard: prevent deleting defaults
    // if (typeDoc.isDefault) return toast.error("Default types can't be deleted.");

    setDeletingTypeId(typeDoc.id);
    try {
      await updateDoc(
        doc(db, `companies/${companyId}/task_types/${typeDoc.id}`),
        {
          deletedAt: serverTimestamp(),
          deletedBy: user?.uid || null,
          // If you prefer hard delete, use deleteDoc instead:
          // (see below)
        },
      );

      // Hard delete:
      // await deleteDoc(doc(db, `companies/${companyId}/task_types/${typeDoc.id}`));

      toast.success("Task type removed.");
    } catch (e) {
      console.error("deleteTaskType failed", e);
      toast.error("Could not remove type.");
    } finally {
      setDeletingTypeId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────
  const getEmpName = (id) => {
    const e = employees[id] || {};
    return e.name || e.fullName || e.displayName || "Unnamed employee";
  };

  const formatDue = (task) => {
    const iso = task?.dueDateISO;
    if (!iso) return "—";
    try {
      return DateTime.fromISO(iso, { zone: companyTimeZone }).toFormat(
        "MMM dd, yyyy",
      );
    } catch {
      return iso;
    }
  };

  const isOverdue = useCallback(
    (task) => {
      if (!task?.dueDateISO) return false;

      const now = DateTime.now()
        .setZone(companyTimeZone)
        .startOf("day");

      const due = DateTime.fromISO(task.dueDateISO, {
        zone: companyTimeZone,
      }).startOf("day");

      return (
        due < now &&
        task.reviewStatus !== "approved" &&
        task.status !== "archived"
      );
    },
    [companyTimeZone],
  );
  const kpis = useMemo(() => {
    const safeStatus = (t) => t.status || "open";
    const safeReview = (t) => t.reviewStatus || "not_submitted";

    return {
      assigned: tasks.length,
      open: tasks.filter((t) => safeStatus(t) === "open").length,
      inProgress: tasks.filter((t) => safeStatus(t) === "in_progress").length,
      completed: tasks.filter((t) => safeStatus(t) === "completed").length,
      // ✅ NEW
      overdue: tasks.filter((t) => isOverdue(t)).length,
      approved: tasks.filter((t) => safeReview(t) === "approved").length,
      rejected: tasks.filter((t) => safeReview(t) === "rejected").length,
    };
  }, [tasks, isOverdue]); // (companyTimeZone affects isOverdue)

  // ✅ Pagination derived
  const totalItems = tasks.length;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalItems / pageSize));
  }, [totalItems, pageSize]);

  // keep page in range when tasks change
  useEffect(() => {
    setPage(1);
  }, [
    companyId,
    selectedEmployeeId,
    selectedWorkSiteId,
    selectedStatus,
    selectedType,
    selectedPriority,
    selectedReview,
    pageSize,
  ]);

  useEffect(() => {
    // If items shrink and current page becomes invalid
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const pagedTasks = useMemo(() => {
    const start = (page - 1) * pageSize;
    return tasks.slice(start, start + pageSize);
  }, [tasks, page, pageSize]);

  const pageStart = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(page * pageSize, totalItems);

  const resetForm = () => {
    setAllowMultiAssign(false);
    setForm({
      title: "",
      description: "",
      type: DEFAULT_TASK_TYPES[0],
      priority: "Medium",
      assigneeId: "",
      assigneeIds: [],
      workSiteId: "",
      dueDate: "",
      photoRequired: false,

      checklistEnabled: false,
      checklistDraft: "",
      checklistItems: [],
    });
  };

  useEffect(() => {
    if (allowMultiAssign) {
      if (form.assigneeId && !form.assigneeIds.includes(form.assigneeId)) {
        setForm((prev) => ({ ...prev, assigneeIds: [prev.assigneeId] }));
      }
    } else {
      if (!form.assigneeId && form.assigneeIds.length) {
        setForm((prev) => ({ ...prev, assigneeId: prev.assigneeIds[0] }));
      }
      if (form.assigneeIds.length > 1) {
        setForm((prev) => ({
          ...prev,
          assigneeIds: prev.assigneeId ? [prev.assigneeId] : [],
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowMultiAssign]);

  const toggleAssignee = (empId) => {
    setForm((prev) => {
      const already = prev.assigneeIds.includes(empId);
      const next = already
        ? prev.assigneeIds.filter((id) => id !== empId)
        : [...prev.assigneeIds, empId];

      return { ...prev, assigneeIds: next };
    });
  };

  const makeGroupId = () =>
    `grp_${Date.now()}_${Math.random()
      .toString(16)
      .slice(2)}`;

  // ─────────────────────────────────────────────────────────────
  // Employee notifications (Task Manager) — same pattern as Leave
  // Writes to: users/{employeeId}/notifications/{deterministicId}
  // ─────────────────────────────────────────────────────────────
  const pushEmployeeNotification = async ({
    employeeId,
    title,
    message,
    type,
    taskId,
    status,
    reviewStatus,
  }) => {
    if (!employeeId || !companyId) return;

    // ✅ deterministic id so it won’t duplicate if clicked twice / refresh
    // Examples:
    // taskAssigned_{taskId}
    // taskReview_{taskId}_approved
    // taskReview_{taskId}_rejected
    let notifId = `task_${taskId}`;

    if (type === "taskAssigned") {
      notifId = `taskAssigned_${taskId}`;
    }

    if (type === "taskApproved" || type === "taskRejected") {
      notifId = `taskReview_${taskId}_${String(reviewStatus || "updated")}`;
    }

    await setDoc(
      doc(db, `users/${employeeId}/notifications`, notifId),
      {
        archived: false,
        read: false,

        companyId,
        employeeId,

        type: type || "taskStatus",
        title,
        message,

        taskId,
        status: status || null,
        reviewStatus: reviewStatus || null,

        createdAt: serverTimestamp(),
      },
      { merge: true },
    );
  };

  const selectAllAssignees = () => {
    setForm((prev) => ({
      ...prev,
      assigneeIds: employeeOptions.map((e) => e.id),
    }));
  };

  const clearAllAssignees = () => {
    setForm((prev) => ({ ...prev, assigneeIds: [] }));
  };

  // ─────────────────────────────────────────────────────────────
  // Create task
  // ─────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────
  // Create task (NOW includes employee notifications)
  // ─────────────────────────────────────────────────────────────
  const handleCreateTask = async () => {
    if (!companyId || !user?.uid) return;

    const title = form.title.trim();
    if (!title) return toast.error("Task title is required.");

    const assigneeIds = allowMultiAssign
      ? form.assigneeIds
      : form.assigneeId
      ? [form.assigneeId]
      : [];

    if (!assigneeIds.length) return toast.error("Select at least 1 employee.");
    if (!form.workSiteId) return toast.error("Select a job site.");
    if (!form.dueDate) return toast.error("Due date is required.");

    if (!PRIORITIES.includes(form.priority))
      return toast.error("Invalid priority.");
    if (!taskTypeOptions.includes(form.type))
      return toast.error("Invalid task type.");

    const siteName =
      workSiteOptions.find((s) => s.id === form.workSiteId)?.name ||
      "Unnamed site";

    const dueDT = DateTime.fromISO(form.dueDate, {
      zone: companyTimeZone,
    }).startOf("day");

    if (!dueDT.isValid) return toast.error("Invalid due date.");

    const checklist =
      form.checklistEnabled && Array.isArray(form.checklistItems)
        ? form.checklistItems.map((it) => ({
            id: it.id,
            text: String(it.text || "").trim(),
            isDone: false,
            doneAt: null,
            doneBy: null,
          }))
        : [];

    setSavingTask(true);

    try {
      const payload = {
        title,
        description: form.description.trim(),
        type: form.type,
        priority: form.priority,

        status: "open",
        reviewStatus: "not_submitted",

        assigneeIds,
        assigneeNames: assigneeIds.map((id) => getEmpName(id)),

        workSiteId: form.workSiteId,
        workSiteName: siteName,

        dueDateISO: dueDT.toISODate(),
        dueAt: new Date(dueDT.toMillis()),

        photoRequired: !!form.photoRequired,

        checklistEnabled: !!form.checklistEnabled,
        checklist,

        completionComment: null,
        completionPhotos: [],

        companyId,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const tasksRef = collection(db, `companies/${companyId}/tasks`);

      // ✅ MULTI-ASSIGN: create one doc per employee (batch)
      if (allowMultiAssign && assigneeIds.length > 1) {
        const batch = writeBatch(db);
        const groupId = makeGroupId();

        // keep the doc ids so we can notify after commit
        const created = [];

        assigneeIds.forEach((empId) => {
          const empName = getEmpName(empId);
          const taskDocRef = doc(tasksRef); // auto id (we can read taskDocRef.id now)

          created.push({ empId, taskId: taskDocRef.id });

          batch.set(taskDocRef, {
            ...payload,

            assigneeId: empId,
            assigneeName: empName,

            // compatibility
            assigneeIds: [empId],
            assigneeNames: [empName],

            groupId,
            isBulkAssigned: true,
          });
        });

        await batch.commit();

        // ✅ Notify each employee (after commit so task exists)
        await Promise.all(
          created.map(({ empId, taskId }) =>
            pushEmployeeNotification({
              employeeId: empId,
              taskId,
              type: "taskAssigned",
              title: "New task assigned",
              message: `You’ve been assigned: "${title}" (Due: ${dueDT.toISODate()}).`,
              status: "open",
              reviewStatus: "not_submitted",
            }),
          ),
        );

        toast.success(`Created ${assigneeIds.length} tasks.`);
      } else {
        // ✅ SINGLE ASSIGN
        const onlyId = assigneeIds[0];

        const newRef = await addDoc(tasksRef, {
          ...payload,

          assigneeId: onlyId,
          assigneeName: getEmpName(onlyId),

          // compatibility
          assigneeIds: [onlyId],
          assigneeNames: [getEmpName(onlyId)],
        });

        // ✅ Notify employee
        await pushEmployeeNotification({
          employeeId: onlyId,
          taskId: newRef.id,
          type: "taskAssigned",
          title: "New task assigned",
          message: `You’ve been assigned: "${title}" (Due: ${dueDT.toISODate()}).`,
          status: "open",
          reviewStatus: "not_submitted",
        });

        toast.success("Task created.");
      }

      setShowCreateModal(false);
      resetForm();
    } catch (e) {
      console.error("Failed to create task", e);
      toast.error("Could not create task. Try again.");
    } finally {
      setSavingTask(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Employer Review Actions
  // ─────────────────────────────────────────────────────────────
  const approveTask = async (task) => {
    if (!companyId || !user?.uid || !task?.id) return;

    try {
      await updateDoc(doc(db, `companies/${companyId}/tasks/${task.id}`), {
        reviewStatus: "approved",
        reviewedAt: serverTimestamp(),
        reviewedBy: user.uid,
        updatedAt: serverTimestamp(),
      });

      // ✅ Notify employee
      await pushEmployeeNotification({
        employeeId: task.assigneeId,
        taskId: task.id,
        type: "taskApproved",
        title: "Task approved",
        message: `Your submission for "${task.title ||
          "your task"}" was approved.`,
        status: task.status || null,
        reviewStatus: "approved",
      });

      toast.success("Task approved.");

      // ✅ update activeTask UI immediately
      setActiveTask((prev) =>
        prev?.id === task.id ? { ...prev, reviewStatus: "approved" } : prev,
      );
    } catch (e) {
      console.error("approveTask failed:", e);
      toast.error("Could not approve task.");
    }
  };

  const confirmRejectTask = async () => {
    if (!companyId || !user?.uid || !rejectingTask?.id) return;

    const reason = String(rejectReason || "").trim();

    try {
      await updateDoc(
        doc(db, `companies/${companyId}/tasks/${rejectingTask.id}`),
        {
          reviewStatus: "rejected",
          rejectionReason: reason || null,
          reviewedAt: serverTimestamp(),
          reviewedBy: user.uid,

          // send back to employee to fix
          status: "in_progress",

          updatedAt: serverTimestamp(),
        },
      );

      // ✅ Notify employee
      await pushEmployeeNotification({
        employeeId: rejectingTask.assigneeId,
        taskId: rejectingTask.id,
        type: "taskRejected",
        title: "Task rejected",
        message: `Your submission for "${rejectingTask.title ||
          "your task"}" was rejected.${reason ? ` Reason: ${reason}` : ""}`,
        status: "in_progress",
        reviewStatus: "rejected",
      });

      toast.success("Task rejected (sent back to employee).");

      // update details modal immediately
      setActiveTask((prev) =>
        prev?.id === rejectingTask.id
          ? {
              ...prev,
              reviewStatus: "rejected",
              rejectionReason: reason || null,
              status: "in_progress",
            }
          : prev,
      );

      closeRejectModal();
    } catch (e) {
      console.error("confirmRejectTask failed:", e);
      toast.error("Could not reject task.");
    }
  };

  /* ----------------------------
     Checklist helpers
     ---------------------------- */
  const CHECKLIST_MAX_ITEMS = 20;
  const CHECKLIST_MAX_CHARS = 80;

  const makeId = () =>
    `${Date.now()}_${Math.random()
      .toString(16)
      .slice(2)}`;

  const addChecklistItem = () => {
    const raw = form.checklistDraft ?? "";
    const text = String(raw).trim();

    if (!form.checklistEnabled) return;

    if (!text) {
      toast.error("Checklist item cannot be empty.");
      return;
    }
    if (text.length > CHECKLIST_MAX_CHARS) {
      toast.error(
        `Checklist item is too long (max ${CHECKLIST_MAX_CHARS} chars).`,
      );
      return;
    }
    if (form.checklistItems.length >= CHECKLIST_MAX_ITEMS) {
      toast.error(`Max ${CHECKLIST_MAX_ITEMS} checklist items.`);
      return;
    }

    // prevent duplicates
    const exists = form.checklistItems.some(
      (it) =>
        String(it.text)
          .trim()
          .toLowerCase() === text.toLowerCase(),
    );
    if (exists) {
      toast.error("That checklist item already exists.");
      return;
    }

    setForm((prev) => ({
      ...prev,
      checklistItems: [...prev.checklistItems, { id: makeId(), text }],
      checklistDraft: "",
    }));
  };

  const removeChecklistItem = (id) => {
    setForm((prev) => ({
      ...prev,
      checklistItems: prev.checklistItems.filter((it) => it.id !== id),
    }));
  };

  const clearChecklist = () => {
    setForm((prev) => ({
      ...prev,
      checklistDraft: "",
      checklistItems: [],
    }));
  };

  if (loadingInit) {
    return <PageLoader label="Loading Task Manager…" />;
  }

  return (
    <div className="taskmgr-page">
      <ToastContainer />
      {showKpis && (
        <div className="taskmgr-kpis">
          <div className="taskmgr-kpiCard taskmgr-kpi-assigned">
            <div className="taskmgr-kpiLabel">Assigned Tasks</div>
            <div className="taskmgr-kpiValue">{kpis.assigned}</div>
          </div>

          <div className="taskmgr-kpiCard taskmgr-kpi-open">
            <div className="taskmgr-kpiLabel">Open</div>
            <div className="taskmgr-kpiValue">{kpis.open}</div>
          </div>

          <div className="taskmgr-kpiCard taskmgr-kpi-progress">
            <div className="taskmgr-kpiLabel">In Progress</div>
            <div className="taskmgr-kpiValue">{kpis.inProgress}</div>
          </div>

          <div className="taskmgr-kpiCard taskmgr-kpi-complete">
            <div className="taskmgr-kpiLabel">Completed</div>
            <div className="taskmgr-kpiValue">{kpis.completed}</div>
          </div>

          <div className="taskmgr-kpiCard taskmgr-kpi-approved">
            <div className="taskmgr-kpiLabel">Approved</div>
            <div className="taskmgr-kpiValue">{kpis.approved}</div>
          </div>

          <div className="taskmgr-kpiCard taskmgr-kpi-rejected">
            <div className="taskmgr-kpiLabel">Rejected</div>
            <div className="taskmgr-kpiValue">{kpis.rejected}</div>
          </div>
          <div className="taskmgr-kpiCard taskmgr-kpi-overdue">
            <div className="taskmgr-kpiLabel">Overdue</div>
            <div className="taskmgr-kpiValue">{kpis.overdue}</div>
          </div>
        </div>
      )}
      <header className="taskmgr-header">
        <div>
          <h1>Task Manager</h1>
          <p>Create, assign, and track tasks by employee and job site.</p>
        </div>

        <div className="taskmgr-headerActions">
          <button
            type="button"
            className="taskmgr-ghostBtn"
            onClick={() => setShowKpis((v) => !v)}
            title={showKpis ? "Hide KPI cards" : "Show KPI cards"}
          >
            {showKpis ? "Hide KPIs" : "Show KPIs"}
          </button>

          <button
            type="button"
            className="taskmgr-create-btn"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Task
          </button>
        </div>
      </header>

      <div className="taskmgr-grid taskmgr-grid--single">
        <section className="taskmgr-card">
          {/* Filters (unchanged) */}
          <div className="taskmgr-filters">
            <div className="taskmgr-filter">
              <label className="taskmgr-label">Employee</label>
              <select
                className="taskmgr-input"
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
              >
                <option value="all">All employees</option>
                {employeeOptions.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name ||
                      emp.fullName ||
                      emp.displayName ||
                      "Unnamed employee"}
                  </option>
                ))}
              </select>
            </div>

            <div className="taskmgr-filter">
              <label className="taskmgr-label">Job site</label>
              <select
                className="taskmgr-input"
                value={selectedWorkSiteId}
                onChange={(e) => setSelectedWorkSiteId(e.target.value)}
                disabled={loadingLocations}
              >
                <option value="all">
                  {loadingLocations ? "Loading job sites…" : "All job sites"}
                </option>

                {workSiteOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="taskmgr-filter">
              <label className="taskmgr-label">Status</label>
              <select
                className="taskmgr-input"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>

            <div className="taskmgr-filter">
              <label className="taskmgr-label">Priority</label>
              <select
                className="taskmgr-input"
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
              >
                <option value="all">All</option>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="taskmgr-filter">
              <label className="taskmgr-label">Review</label>
              <select
                className="taskmgr-input"
                value={selectedReview}
                onChange={(e) => setSelectedReview(e.target.value)}
              >
                <option value="all">All</option>

                {REVIEW_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {REVIEW_LABELS[s] || s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ✅ TABLE LIST VIEW */}
          <div className="taskmgr-list">
            {loadingTasks ? (
              <div style={{ padding: 30 }}>
                <PageLoader label="Loading tasks…" />
              </div>
            ) : tasks.length === 0 ? (
              <div className="ex-empty">
                <img
                  className="ex-empty__img"
                  src={NoDataIllustration}
                  alt="No tasks yet"
                />

                <h3 className="ex-empty__title">No tasks yet</h3>

                <p className="ex-empty__text">
                  Create, assign, and track tasks by employee and job site all
                  in one place.
                </p>
              </div>
            ) : (
              <div className="taskmgr-tableWrap">
                <table className="taskmgr-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Job Site</th>
                      <th>Status</th>
                      <th>Type</th>
                      <th>Priority</th>
                      <th>Due</th>
                      <th>Review</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pagedTasks.map((t) => {
                      const reviewStatus = t.reviewStatus || "not_submitted";
                      const empLabel =
                        t.assigneeName ||
                        (t.assigneeId ? getEmpName(t.assigneeId) : null) ||
                        (Array.isArray(t.assigneeNames) &&
                          t.assigneeNames[0]) ||
                        "—";

                      return (
                        <tr
                          key={t.id}
                          className={`taskmgr-rowClick ${
                            isOverdue(t) ? "isOverdue" : ""
                          }`}
                          tabIndex={0}
                          role="button"
                          onClick={() => openTaskDetails(t)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              openTaskDetails(t);
                          }}
                          title="Click to view task details"
                        >
                          {/* 1) Employee */}
                          <td className="taskmgr-tdWrap">{empLabel}</td>

                          {/* 2) Job Site */}
                          <td className="taskmgr-tdWrap">
                            {t.workSiteName || "—"}
                          </td>

                          {/* 3) Status */}
                          <td>
                            <span className="taskmgr-pill status">
                              {(t.status || "open").replace("_", " ")}
                            </span>
                            {isOverdue(t) && (
                              <span
                                className="taskmgr-pill overdue"
                                style={{ marginLeft: 6 }}
                              >
                                Overdue
                              </span>
                            )}
                          </td>

                          {/* 4) Type */}
                          <td>
                            <span className="taskmgr-pill type">
                              {t.type || "—"}
                            </span>
                          </td>

                          {/* 5) Priority */}
                          <td>
                            <span
                              className={`taskmgr-pill pri-${String(
                                t.priority || "",
                              ).toLowerCase()}`}
                            >
                              {t.priority || "—"}
                            </span>
                          </td>

                          {/* 6) Due */}
                          <td
                            className={isOverdue(t) ? "taskmgr-dueOverdue" : ""}
                          >
                            {formatDue(t)}
                          </td>

                          {/* 7) Review (LAST) */}
                          <td>
                            <span
                              className={`taskmgr-pill review review-${reviewStatus}`}
                            >
                              {reviewStatus === "not_submitted"
                                ? "Not submitted"
                                : reviewStatus === "submitted"
                                ? "Under review"
                                : reviewStatus === "approved"
                                ? "Approved"
                                : "Rejected"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="taskmgr-tableHint">
                  Tip: Click a row to view full details, proof, checklist, and
                  approve/reject.
                </div>
                {/* ✅ Pagination controls */}
                <div className="taskmgr-pagination">
                  <div className="taskmgr-paginationLeft">
                    <span className="taskmgr-paginationInfo">
                      Showing <strong>{pageStart}</strong>-
                      <strong>{pageEnd}</strong> of{" "}
                      <strong>{totalItems}</strong>
                    </span>

                    <label className="taskmgr-paginationSize">
                      Rows:
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </label>
                  </div>

                  <div className="taskmgr-paginationRight">
                    <button
                      type="button"
                      className="taskmgr-pageBtn"
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                      title="First page"
                    >
                      «
                    </button>

                    <button
                      type="button"
                      className="taskmgr-pageBtn"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      title="Previous page"
                    >
                      ‹
                    </button>

                    <span className="taskmgr-pageMeta">
                      Page <strong>{page}</strong> /{" "}
                      <strong>{totalPages}</strong>
                    </span>

                    <button
                      type="button"
                      className="taskmgr-pageBtn"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                      title="Next page"
                    >
                      ›
                    </button>

                    <button
                      type="button"
                      className="taskmgr-pageBtn"
                      onClick={() => setPage(totalPages)}
                      disabled={page === totalPages}
                      title="Last page"
                    >
                      »
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* ✅ TASK DETAILS MODAL */}
      {showDetailsModal && activeTask && (
        <div
          className="taskmgr-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target.classList.contains("taskmgr-modal-backdrop")) {
              closeTaskDetails();
            }
          }}
        >
          <div className="taskmgr-modal" role="dialog" aria-modal="true">
            <div className="taskmgr-modal-header">
              <div>
                <h3 className="taskmgr-modal-title">
                  {activeTask.title || "Untitled task"}
                </h3>
                <p className="taskmgr-modal-subtitle">
                  Job site: <strong>{activeTask.workSiteName || "—"}</strong> •
                  Due: <strong>{formatDue(activeTask)}</strong>
                </p>
              </div>

              <button
                type="button"
                className="taskmgr-icon-btn"
                onClick={closeTaskDetails}
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="taskmgr-modal-body">
              {/* Pills */}
              <div className="taskmgr-detailPills">
                <span className="taskmgr-pill status">
                  {(activeTask.status || "open").replace("_", " ")}
                </span>

                <span className="taskmgr-pill type">
                  {activeTask.type || "—"}
                </span>

                <span
                  className={`taskmgr-pill pri-${String(
                    activeTask.priority || "",
                  ).toLowerCase()}`}
                >
                  {activeTask.priority || "—"}
                </span>

                <span
                  className={`taskmgr-pill review review-${activeTask.reviewStatus ||
                    "not_submitted"}`}
                >
                  {(activeTask.reviewStatus || "not_submitted") ===
                  "not_submitted"
                    ? "Not submitted"
                    : (activeTask.reviewStatus || "not_submitted") ===
                      "submitted"
                    ? "Under review"
                    : (activeTask.reviewStatus || "not_submitted") ===
                      "approved"
                    ? "Approved"
                    : "Rejected"}
                </span>

                {activeTask.photoRequired && (
                  <span className="taskmgr-pill proof">Photo required</span>
                )}
                {isOverdue(activeTask) && (
                  <span className="taskmgr-pill overdue">Overdue</span>
                )}
              </div>

              {/* Assigned / meta */}
              <div className="taskmgr-meta" style={{ marginTop: 12 }}>
                <div>
                  <strong>Assigned:</strong>{" "}
                  {Array.isArray(activeTask.assigneeNames) &&
                  activeTask.assigneeNames.length
                    ? activeTask.assigneeNames.join(", ")
                    : Array.isArray(activeTask.assigneeIds) &&
                      activeTask.assigneeIds.length
                    ? activeTask.assigneeIds.map(getEmpName).join(", ")
                    : "—"}
                </div>
                <div>
                  <strong>Job site:</strong> {activeTask.workSiteName || "—"}
                </div>
                <div>
                  <strong>Due:</strong> {formatDue(activeTask)}
                </div>
              </div>

              {/* Description */}
              {activeTask.description ? (
                <div style={{ marginTop: 12 }}>
                  <div className="taskmgr-label" style={{ marginTop: 0 }}>
                    Description
                  </div>
                  <div className="taskmgr-empty taskmgr-boxSolid">
                    {activeTask.description}
                  </div>
                </div>
              ) : null}

              {/* Checklist (read-only view) */}
              {activeTask.checklistEnabled &&
              Array.isArray(activeTask.checklist) &&
              activeTask.checklist.length > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <div className="taskmgr-label" style={{ marginTop: 0 }}>
                    Checklist
                  </div>

                  <div className="taskmgr-empty taskmgr-boxSolid">
                    {activeTask.checklist.map((it, idx) => (
                      <div key={it.id || idx} className="taskmgr-checklistLine">
                        <span className="taskmgr-checklistNum">{idx + 1}.</span>
                        <span
                          className={`taskmgr-checklistLineText ${
                            it.isDone ? "done" : ""
                          }`}
                        >
                          {it.text} {it.isDone ? "✅" : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Submission info (comment + proof + rejection reason) */}
              {(activeTask.reviewStatus || "not_submitted") !==
                "not_submitted" && (
                <div style={{ marginTop: 12 }}>
                  <div className="taskmgr-label" style={{ marginTop: 0 }}>
                    Submission
                  </div>

                  <div className="taskmgr-empty taskmgr-boxSolid">
                    <div>
                      <strong>Comment:</strong>{" "}
                      {activeTask.completionComment
                        ? activeTask.completionComment
                        : "—"}
                    </div>

                    {Array.isArray(activeTask.completionPhotos) &&
                      activeTask.completionPhotos.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <strong>Proof:</strong>{" "}
                          {activeTask.completionPhotos.map((ph, idx) => (
                            <button
                              key={`${ph.url}_${idx}`}
                              type="button"
                              className="taskmgr-proofLink"
                              onClick={() => openProofModal(ph, idx)}
                            >
                              Photo {idx + 1}
                            </button>
                          ))}
                        </div>
                      )}

                    {(activeTask.reviewStatus || "not_submitted") ===
                      "rejected" &&
                      activeTask.rejectionReason && (
                        <div style={{ marginTop: 10 }}>
                          <strong>Rejection reason:</strong>{" "}
                          {activeTask.rejectionReason}
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>

            {/* Approve/Reject in modal footer */}
            <div className="taskmgr-modal-footer">
              <button
                type="button"
                className="taskmgr-secondary"
                onClick={closeTaskDetails}
              >
                Close
              </button>

              {(activeTask.reviewStatus || "not_submitted") === "submitted" && (
                <>
                  <button
                    type="button"
                    className="taskmgr-rejectBtn"
                    onClick={() => openRejectModal(activeTask)}
                  >
                    Reject
                  </button>

                  <button
                    type="button"
                    className="taskmgr-approveBtn"
                    onClick={() => approveTask(activeTask)}
                  >
                    Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CREATE TASK MODAL (UNCHANGED) */}
      {showCreateModal && (
        <div
          className="taskmgr-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target.classList.contains("taskmgr-modal-backdrop")) {
              setShowCreateModal(false);
            }
          }}
        >
          <div className="taskmgr-modal" role="dialog" aria-modal="true">
            <div className="taskmgr-modal-header">
              <div>
                <h3 className="taskmgr-modal-title">Create Task</h3>
                <p className="taskmgr-modal-subtitle">
                  Fill out the details and assign employees. Uses company
                  timezone: <strong>{companyTimeZone}</strong>.
                </p>
              </div>

              <button
                type="button"
                className="taskmgr-icon-btn"
                onClick={() => setShowCreateModal(false)}
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="taskmgr-modal-body">
              <div className="taskmgr-modal-section">
                <h4>Task Info</h4>
                <label className="taskmgr-label">Task title</label>
                <input
                  className="taskmgr-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Inspect fire extinguisher"
                />
                <label className="taskmgr-label">Task description</label>
                <textarea
                  className="taskmgr-textarea"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Instructions (optional)"
                />

                <div className="taskmgr-checklist">
                  <div className="taskmgr-toggle-row" style={{ marginTop: 10 }}>
                    <span className="taskmgr-toggle-label">
                      Add checklist (optional)
                    </span>

                    <label className="taskmgr-switch">
                      <input
                        type="checkbox"
                        checked={!!form.checklistEnabled}
                        onChange={(e) =>
                          setForm((prev) => {
                            const enabled = e.target.checked;
                            return {
                              ...prev,
                              checklistEnabled: enabled,
                              ...(enabled
                                ? {}
                                : { checklistDraft: "", checklistItems: [] }),
                            };
                          })
                        }
                      />
                      <span className="taskmgr-switch-slider" />
                    </label>
                  </div>

                  {form.checklistEnabled && (
                    <>
                      <div className="taskmgr-checklistRow">
                        <input
                          className="taskmgr-input"
                          value={form.checklistDraft}
                          placeholder="Type a checklist item… (press Enter to add)"
                          maxLength={CHECKLIST_MAX_CHARS}
                          onChange={(e) =>
                            setForm({ ...form, checklistDraft: e.target.value })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addChecklistItem();
                            }
                          }}
                        />

                        <button
                          type="button"
                          className="taskmgr-checklistAdd"
                          onClick={addChecklistItem}
                        >
                          Add
                        </button>
                      </div>

                      <div className="taskmgr-hint" style={{ marginTop: 8 }}>
                        Add up to <strong>{CHECKLIST_MAX_ITEMS}</strong> items.
                        Each item max <strong>{CHECKLIST_MAX_CHARS}</strong>{" "}
                        characters.
                      </div>

                      {form.checklistItems.length > 0 ? (
                        <div className="taskmgr-checklistList">
                          {form.checklistItems.map((it, idx) => (
                            <div key={it.id} className="taskmgr-checklistItem">
                              <div className="taskmgr-checklistLeft">
                                <span className="taskmgr-checklistIndex">
                                  {idx + 1}.
                                </span>
                                <span className="taskmgr-checklistText">
                                  {it.text}
                                </span>
                              </div>

                              <button
                                type="button"
                                className="taskmgr-checklistRemove"
                                onClick={() => removeChecklistItem(it.id)}
                                title="Remove item"
                                aria-label="Remove item"
                              >
                                ✕
                              </button>
                            </div>
                          ))}

                          <div className="taskmgr-checklistFooter">
                            <span className="taskmgr-checklistCount">
                              Items:{" "}
                              <strong>{form.checklistItems.length}</strong>
                            </span>

                            <button
                              type="button"
                              className="taskmgr-miniBtn"
                              onClick={clearChecklist}
                              style={{ marginLeft: "auto" }}
                            >
                              Clear checklist
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="taskmgr-checklistEmpty">
                          No checklist items yet. Add your first one above.
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="taskmgr-toggle-row" style={{ marginTop: 10 }}>
                  <span className="taskmgr-toggle-label">
                    Require photo proof on completion
                  </span>

                  <label className="taskmgr-switch">
                    <input
                      type="checkbox"
                      checked={!!form.photoRequired}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          photoRequired: e.target.checked,
                        }))
                      }
                    />
                    <span className="taskmgr-switch-slider" />
                  </label>
                </div>

                <div className="taskmgr-hint">
                  If enabled, the employee must upload a photo before submitting
                  the task.
                </div>

                <div className="taskmgr-row">
                  <div className="taskmgr-col">
                    <label className="taskmgr-label">Task type</label>

                    <div className="taskmgr-inlineSelect">
                      <select
                        className="taskmgr-input"
                        value={form.type}
                        onChange={(e) =>
                          setForm({ ...form, type: e.target.value })
                        }
                        disabled={loadingTypes}
                      >
                        {taskTypeOptions.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        className="taskmgr-miniBtn"
                        onClick={() => setShowTypesModal(true)}
                        title="Manage task types"
                      >
                        Manage
                      </button>
                    </div>
                  </div>

                  <div className="taskmgr-col">
                    <label className="taskmgr-label">Priority</label>
                    <select
                      className="taskmgr-input"
                      value={form.priority}
                      onChange={(e) =>
                        setForm({ ...form, priority: e.target.value })
                      }
                    >
                      {PRIORITIES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="taskmgr-modal-section">
                <h4>Assign</h4>

                <div className="taskmgr-toggle-row">
                  <span className="taskmgr-toggle-label">
                    Assign multiple employees
                  </span>

                  <label className="taskmgr-switch">
                    <input
                      type="checkbox"
                      checked={allowMultiAssign}
                      onChange={(e) => setAllowMultiAssign(e.target.checked)}
                    />
                    <span className="taskmgr-switch-slider" />
                  </label>
                </div>

                {!allowMultiAssign ? (
                  <>
                    <label className="taskmgr-label">Employee</label>
                    <select
                      className="taskmgr-input"
                      value={form.assigneeId}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          assigneeId: e.target.value,
                          assigneeIds: [],
                        })
                      }
                    >
                      <option value="">Select an employee</option>
                      {employeeOptions.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name ||
                            emp.fullName ||
                            emp.displayName ||
                            "Unnamed employee"}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <>
                    <label className="taskmgr-label">Employees</label>
                    <div className="taskmgr-multiAssignBox">
                      <div className="taskmgr-multiAssignTop">
                        <button
                          type="button"
                          className="taskmgr-miniBtn"
                          onClick={selectAllAssignees}
                        >
                          Select all
                        </button>

                        <button
                          type="button"
                          className="taskmgr-miniBtn"
                          onClick={clearAllAssignees}
                        >
                          Clear
                        </button>

                        <span className="taskmgr-multiAssignCount">
                          Selected: <strong>{form.assigneeIds.length}</strong>
                        </span>
                      </div>

                      <div className="taskmgr-multiAssignList" role="list">
                        {employeeOptions.map((emp) => {
                          const label =
                            emp.name ||
                            emp.fullName ||
                            emp.displayName ||
                            "Unnamed employee";
                          const checked = form.assigneeIds.includes(emp.id);

                          return (
                            <label
                              key={emp.id}
                              className="taskmgr-checkRow"
                              role="listitem"
                            >
                              <input
                                type="checkbox"
                                className="taskmgr-checkbox"
                                checked={checked}
                                onChange={() => toggleAssignee(emp.id)}
                              />
                              <span className="taskmgr-checkLabel">
                                {label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}

                <label className="taskmgr-label" style={{ marginTop: 10 }}>
                  Location / Job site
                </label>
                <select
                  className="taskmgr-input"
                  value={form.workSiteId}
                  onChange={(e) =>
                    setForm({ ...form, workSiteId: e.target.value })
                  }
                  disabled={loadingLocations}
                >
                  <option value="">
                    {loadingLocations
                      ? "Loading job sites…"
                      : "Select a job site"}
                  </option>

                  {workSiteOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="taskmgr-modal-section">
                <h4>Timing</h4>
                <label className="taskmgr-label">Due date</label>
                <input
                  type="date"
                  className="taskmgr-input"
                  value={form.dueDate}
                  onChange={(e) =>
                    setForm({ ...form, dueDate: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="taskmgr-modal-footer">
              <button
                type="button"
                className="taskmgr-secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                disabled={savingTask}
              >
                Cancel
              </button>

              <button
                type="button"
                className="taskmgr-primary"
                onClick={handleCreateTask}
                disabled={savingTask}
              >
                {savingTask ? "Creating…" : "Publish Task"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showRejectModal && rejectingTask && (
        <div
          className="taskmgr-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target.classList.contains("taskmgr-modal-backdrop")) {
              closeRejectModal();
            }
          }}
        >
          <div className="taskmgr-modal" role="dialog" aria-modal="true">
            <div className="taskmgr-modal-header">
              <div>
                <h3 className="taskmgr-modal-title">Reject Task</h3>
                <p className="taskmgr-modal-subtitle">
                  Add a note so the employee knows what to fix (optional).
                </p>
              </div>

              <button
                type="button"
                className="taskmgr-icon-btn"
                onClick={closeRejectModal}
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="taskmgr-modal-body">
              <label className="taskmgr-label">
                Reason for rejection (optional)
              </label>
              <textarea
                className="taskmgr-textarea"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., Photo is blurry. Please retake showing the full window."
              />
            </div>

            <div className="taskmgr-modal-footer">
              <button
                type="button"
                className="taskmgr-secondary"
                onClick={closeRejectModal}
              >
                Cancel
              </button>

              <button
                type="button"
                className="taskmgr-rejectBtn"
                onClick={confirmRejectTask}
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
      {showTypesModal && (
        <div
          className="taskmgr-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target.classList.contains("taskmgr-modal-backdrop")) {
              setShowTypesModal(false);
            }
          }}
        >
          <div className="taskmgr-modal" role="dialog" aria-modal="true">
            <div className="taskmgr-modal-header">
              <div>
                <h3 className="taskmgr-modal-title">Manage Task Types</h3>
                <p className="taskmgr-modal-subtitle">
                  Add or remove task types for your company.
                </p>
              </div>

              <button
                type="button"
                className="taskmgr-icon-btn"
                onClick={() => setShowTypesModal(false)}
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="taskmgr-modal-body">
              <label className="taskmgr-label">Add new type</label>

              <div className="taskmgr-checklistRow">
                <input
                  className="taskmgr-input"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  placeholder="e.g., Inventory, Safety, Maintenance"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTaskType();
                    }
                  }}
                />

                <button
                  type="button"
                  className="taskmgr-checklistAdd"
                  onClick={addTaskType}
                  disabled={savingType}
                >
                  {savingType ? "Adding…" : "Add"}
                </button>
              </div>

              <div style={{ marginTop: 14 }}>
                <div className="taskmgr-label" style={{ marginTop: 0 }}>
                  Current types
                </div>

                <div className="taskmgr-checklistList">
                  {(taskTypes?.length
                    ? taskTypes
                    : DEFAULT_TASK_TYPES.map((n) => ({
                        id: n,
                        name: n,
                        isDefault: true,
                      }))
                  )
                    .filter((t) => !t.deletedAt) // ignore soft-deleted (if you used that)
                    .map((t) => (
                      <div key={t.id} className="taskmgr-checklistItem">
                        <div className="taskmgr-checklistLeft">
                          <span className="taskmgr-checklistText">
                            {t.name}
                          </span>
                          {t.isDefault && (
                            <span
                              className="taskmgr-pill type"
                              style={{ marginLeft: 8 }}
                            >
                              Default
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          className="taskmgr-checklistRemove"
                          onClick={() => deleteTaskType(t)}
                          disabled={deletingTypeId === t.id}
                          title="Remove type"
                          aria-label="Remove type"
                        >
                          {deletingTypeId === t.id ? "…" : "✕"}
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="taskmgr-modal-footer">
              <button
                type="button"
                className="taskmgr-secondary"
                onClick={() => setShowTypesModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ✅ PROOF PREVIEW MODAL */}
      {showProofModal && activeProof?.url && (
        <div
          className="taskmgr-proof-backdrop"
          onMouseDown={(e) => {
            if (e.target.classList.contains("taskmgr-proof-backdrop")) {
              closeProofModal();
            }
          }}
        >
          <div className="taskmgr-proof-modal" role="dialog" aria-modal="true">
            <div className="taskmgr-proof-header">
              <div className="taskmgr-proof-title">
                {activeProof.name || "Proof photo"}
              </div>

              <button
                type="button"
                className="taskmgr-icon-btn"
                onClick={closeProofModal}
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="taskmgr-proof-body">
              <img
                src={activeProof.url}
                alt={activeProof.name || "Proof"}
                className="taskmgr-proof-img"
                loading="lazy"
              />
            </div>

            <div className="taskmgr-proof-footer">
              {/*<a
                className="taskmgr-secondary"
                href={activeProof.url}
                target="_blank"
                rel="noreferrer"
              >
                Open in new tab
              </a>*/}

              <button
                type="button"
                className="taskmgr-primary"
                onClick={closeProofModal}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
